import { x25519 } from '@noble/curves/ed25519';
import { defineStore } from 'pinia';
import { useAccountStore } from '~/stores/account';
import { useDeviceActivityStore } from '~/stores/deviceActivity';
import { useEngineLabelsStore } from '~/stores/engineLabels';
import { useOrgKeyUnlockStore } from '~/stores/orgKeyUnlock';
import { deriveAccountKeysFromHub, unwrapPrivateKey } from '~/util/accountCrypto';
import { base64ToBytes, bytesToBase64 } from '~/util/bytes';
import { forgetHeldActivations, restoreSharedKeys } from '~/util/keyVault';
import { endSessionWork, sessionStill } from '~/util/sessionEpoch';
import { forgetPins } from '~/util/pins';
import * as hubClient from '~/util/hubClient';
import { completeSignIn as completeSecondFactor } from '~/util/secondFactor';
/** The sign-in waiting for its code: its token, and how to open the vault
 * once the session (with the vault) arrives. Kept out of the store's state
 * so the key material isn't in anything reactive or inspectable. */
let pendingSignIn = null;
const CURRENT_ORG_KEY = 'zunox-current-organization';
function readCurrentOrg() {
    try {
        return localStorage.getItem(CURRENT_ORG_KEY);
    }
    catch {
        return null;
    }
}
function writeCurrentOrg(id) {
    try {
        if (id)
            localStorage.setItem(CURRENT_ORG_KEY, id);
        else
            localStorage.removeItem(CURRENT_ORG_KEY);
    }
    catch {
        // Remembered for this page load only.
    }
}
/** Everything decrypted or unlocked in this tab goes when the user changes. */
export { sessionStill };
/** The account key this tab holds belongs to the session's person: their
 * vault's public key is that key's. Anything else (another person signed in
 * from another tab, a session with no vault) is not theirs to use. */
function vaultIsTheirs(session) {
    const held = useAccountStore().privateKeyBase64;
    if (!held)
        return true;
    const publicKey = session?.vault?.public_key;
    if (!publicKey)
        return false;
    try {
        return bytesToBase64(x25519.getPublicKey(base64ToBytes(held))) === publicKey;
    }
    catch {
        return false;
    }
}
/** Whose this tab is: the person whose session put secrets in it. */
const TAB_OWNER = 'zunox-tab-owner';
function readTabOwner() {
    try {
        return sessionStorage.getItem(TAB_OWNER);
    }
    catch {
        return null;
    }
}
function writeTabOwner(userId) {
    try {
        if (userId)
            sessionStorage.setItem(TAB_OWNER, userId);
        else
            sessionStorage.removeItem(TAB_OWNER);
    }
    catch {
        // Storage blocked: nothing is kept in it to protect either.
    }
}
/** Whether this tab holds an account key or any client key. */
function tabHoldsSecrets() {
    if (useAccountStore().privateKeyBase64)
        return true;
    return Object.keys(useOrgKeyUnlockStore().keys).length > 0;
}
function forgetLocalSecrets() {
    endSessionWork();
    writeTabOwner(null);
    forgetPins();
    forgetHeldActivations();
    useAccountStore().closeVault();
    useOrgKeyUnlockStore().lockAll();
    forgetOrganizationData();
    forgetRemembered();
    try {
        // The client just made (Firm page), with its engine names.
        sessionStorage.removeItem('zunox:new-client');
    }
    catch {
        // Storage blocked: nothing was kept there either.
    }
}
/** What this app remembered about the person's own use in this browser,
 * gone on sign-out so the next person at a shared computer learns nothing
 * from it: which client was open, and who was emailed a readout. Named one
 * by one, never by pattern: everything else this app keeps is a safeguard
 * that must outlive a sign-out and is left alone. The pins (zunox:pins:*:
 * the identities this browser has verified and the highest pins version it
 * has trusted, which is what catches a Hub serving an older copy), the
 * newest-result watermarks (zunox:newest:*, which catch a Hub holding a
 * result back) and the engine account chosen for each client (whose
 * signature results must carry) all stay. */
export const FORGOTTEN_ON_SIGN_OUT = ['zunox-current-organization', 'zunox-release-people:'];
function forgetRemembered() {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && FORGOTTEN_ON_SIGN_OUT.some(p => (p.endsWith(':') ? k.startsWith(p) : k === p)))
                keys.push(k);
        }
        keys.forEach(k => localStorage.removeItem(k));
    }
    catch {
        // Storage blocked: nothing was remembered there either.
    }
}
/** Drops one organization's loaded devices and activity, before another's
 * are shown. */
function forgetOrganizationData() {
    useEngineLabelsStore().forget();
    const devices = useDeviceActivityStore();
    devices.clearDecrypted();
    devices.$reset();
}
export const useHubStore = defineStore('hub', {
    state: () => ({
        session: null,
        organizations: [],
        sessionChecked: false,
        hubReachable: true,
        currentOrganizationId: readCurrentOrg(),
        secondFactorPending: false,
    }),
    getters: {
        /** The client organization being worked on: the one chosen in the
         * header, or the first the user may open. Named for the single
         * organization this used to be; every page reads it. */
        defaultOrganization(state) {
            return (state.organizations.find(o => o.id === state.currentOrganizationId) ??
                state.organizations.find(o => !o.archived) ??
                null);
        },
        /** Clients still being worked on. Archived ones (which only firm
         * admins get) are listed under Firm, not in the sidebar. */
        activeOrganizations(state) {
            return state.organizations.filter(o => !o.archived);
        },
        isFirmAdmin(state) {
            return !!state.session?.is_firm_admin;
        },
        displayName(state) {
            return state.session?.display_name || state.session?.email || '';
        },
    },
    actions: {
        /** Picks up an existing session, if there is one. Signed out is not an
         * error: the app shows the sign-in page. */
        async refreshSession() {
            let still = sessionStill();
            let session;
            try {
                session = await hubClient.getSession();
                if (!still())
                    return;
                this.hubReachable = true;
            }
            catch (err) {
                if (!still())
                    return;
                // Signed out on the Hub (expired, revoked, signed out elsewhere): the
                // keys go with the session. Any other failure (a proxy's 502, the Hub
                // unreachable) forgets nothing: it may be back in a moment.
                if (err instanceof hubClient.HubApiError && err.status === 401)
                    forgetLocalSecrets();
                this.session = null;
                this.hubReachable = err instanceof hubClient.HubApiError && err.status !== undefined;
                this.organizations = [];
                this.sessionChecked = true;
                return;
            }
            // Another person's session (signed in from another tab), or none: the
            // keys this tab holds are not theirs, and go before anything else. A
            // tab remembers whose it is; secrets in a tab that can't say are
            // treated as someone else's.
            const owner = readTabOwner();
            const someoneElse = (this.session !== null && this.session.user_id !== session?.user_id) ||
                !vaultIsTheirs(session) ||
                (owner !== null && owner !== session?.user_id) ||
                // A tab from before owners were recorded: its account key, when it
                // matches the person's vault, proves whose it is; client keys alone
                // prove nothing.
                (owner === null && tabHoldsSecrets() && !useAccountStore().privateKeyBase64);
            if (someoneElse) {
                forgetLocalSecrets();
                still = sessionStill();
            }
            this.session = session;
            if (session)
                writeTabOwner(session.user_id);
            if (this.session) {
                try {
                    await this.loadOrganizations(still);
                }
                catch {
                    if (!still())
                        return;
                    this.organizations = [];
                }
                if (!still())
                    return;
                // A reload keeps the vault open for the tab: unlock its clients again.
                await restoreSharedKeys();
            }
            else {
                this.organizations = [];
            }
            if (!still())
                return;
            this.sessionChecked = true;
        },
        async loadOrganizations(still = sessionStill()) {
            const organizations = await hubClient.listOrganizations();
            if (!still())
                return;
            this.organizations = organizations;
            const current = this.organizations.find(o => o.id === this.currentOrganizationId);
            if (!current) {
                this.selectOrganization(this.organizations.find(o => !o.archived)?.id ?? null);
            }
        },
        /** Switches the client organization being worked on. */
        selectOrganization(id) {
            if (id === this.currentOrganizationId)
                return;
            forgetOrganizationData();
            this.currentOrganizationId = id;
            writeCurrentOrg(id);
        },
        /** Signs in with email and password. The password is stretched here
         * and never sent (see `accountCrypto`); the vault opens with it. False
         * when an authenticator code is needed next (`completeSignIn`). */
        async signIn(email, password, onProgress) {
            const kdf = await hubClient.prelogin(email);
            const { authKey, vaultKey } = await deriveAccountKeysFromHub(password, kdf, onProgress);
            const reply = await hubClient.login(email, bytesToBase64(authKey));
            return this.adoptSignIn(reply, (session) => session.vault
                ? unwrapPrivateKey(vaultKey, session.vault.wrapped_private_key)
                : Promise.resolve(null));
        },
        /** Takes the reply to a sign-in: starts the session, or (when a code
         * is needed) keeps it waiting for `completeSignIn`. True once signed in. */
        async adoptSignIn(reply, openVault) {
            if (hubClient.needsSecondFactor(reply)) {
                pendingSignIn = { token: reply.second_factor.token, openVault };
                this.secondFactorPending = true;
                return false;
            }
            await this.startSession(reply, await openVault(reply));
            return true;
        },
        /** The second step of a waiting sign-in. */
        async completeSignIn(code) {
            if (!pendingSignIn)
                throw new Error('Sign in with your password first.');
            const session = await completeSecondFactor(pendingSignIn.token, code);
            const privateKey = await pendingSignIn.openVault(session);
            this.cancelSignIn();
            await this.startSession(session, privateKey);
        },
        cancelSignIn() {
            pendingSignIn = null;
            this.secondFactorPending = false;
        },
        /** Adopts a session just created (by signing in, the first admin's
         * setup, or accepting an invite) and the private key opened with it. */
        async startSession(session, privateKey) {
            if (this.session?.user_id !== session.user_id || readTabOwner() !== session.user_id) {
                forgetLocalSecrets();
            }
            writeTabOwner(session.user_id);
            const still = sessionStill();
            if (privateKey)
                useAccountStore().openVault(privateKey);
            this.session = session;
            this.hubReachable = true;
            this.sessionChecked = true;
            await this.loadOrganizations(still);
            if (!still())
                return;
            await restoreSharedKeys();
        },
        /** Opens a signed-in user's vault again with their password (after the
         * tab was closed: the session lasts longer than the tab), and unlocks
         * every client key shared with them. */
        async unlockVault(password, onProgress) {
            const vault = this.session?.vault;
            if (!vault)
                throw new Error('This account has no password to unlock with.');
            // Signed out (or someone else signed in) while the password was being
            // stretched: the vault stays shut.
            const still = sessionStill();
            const userId = this.session?.user_id;
            const { vaultKey } = await deriveAccountKeysFromHub(password, vault.kdf, onProgress);
            const privateKey = await unwrapPrivateKey(vaultKey, vault.wrapped_private_key);
            if (!still() || this.session?.user_id !== userId) {
                throw new Error('Signed out while unlocking: nothing was opened.');
            }
            useAccountStore().openVault(privateKey);
            await restoreSharedKeys();
        },
        async signOut() {
            // Everything begun for this person ends now, not when the Hub answers.
            forgetLocalSecrets();
            this.cancelSignIn();
            this.session = null;
            this.organizations = [];
            this.currentOrganizationId = null;
            const still = sessionStill();
            try {
                await hubClient.logout();
            }
            finally {
                // Once more for anything the person started meanwhile; but if the
                // next person has signed in since (their sign-in waited for this
                // sign-out to reach the Hub), theirs is left alone.
                if (still()) {
                    forgetLocalSecrets();
                    this.session = null;
                    this.organizations = [];
                    this.currentOrganizationId = null;
                }
            }
        },
        async getOrgKey(organizationId) {
            return hubClient.getOrgKey(organizationId);
        },
        async publishOrgKey(organizationId, publicKeyBase64, signature) {
            return hubClient.publishOrgKey(organizationId, publicKeyBase64, signature);
        },
        async setUpOrgIdentity(organizationId, identityPublicKey, publicKeyBase64, signature) {
            return hubClient.setUpOrgIdentity(organizationId, identityPublicKey, publicKeyBase64, signature);
        },
        async listOrgKeyHistory(organizationId) {
            return hubClient.listOrgKeyHistory(organizationId);
        },
        async rollbackOrgKey(organizationId, keyVersion, signature) {
            return hubClient.rollbackOrgKey(organizationId, keyVersion, signature);
        },
        async listKeyVersionUsage(organizationId) {
            return hubClient.listKeyVersionUsage(organizationId);
        },
        async listRecordsForKeyVersion(organizationId, keyVersion, after, limit) {
            return hubClient.listRecordsForKeyVersion(organizationId, keyVersion, after, limit);
        },
        async reencryptRecords(organizationId, fromKeyVersion, records) {
            return hubClient.reencryptRecords(organizationId, fromKeyVersion, records);
        },
        async listDayBundlesForKeyVersion(organizationId, keyVersion, after, limit) {
            return hubClient.listDayBundlesForKeyVersion(organizationId, keyVersion, after, limit);
        },
        async reencryptDayBundles(organizationId, fromKeyVersion, records) {
            return hubClient.reencryptDayBundles(organizationId, fromKeyVersion, records);
        },
        async approveDevice(organizationId, deviceId, endorsement = null) {
            return hubClient.approveDevice(organizationId, deviceId, endorsement);
        },
        async endorseDevice(organizationId, deviceId, endorsement) {
            return hubClient.endorseDevice(organizationId, deviceId, endorsement);
        },
        async listDevices(organizationId) {
            return hubClient.listDevices(organizationId);
        },
        async revokeDevice(organizationId, deviceId) {
            return hubClient.revokeDevice(organizationId, deviceId);
        },
        async purgeDevice(organizationId, deviceId) {
            return hubClient.purgeDevice(organizationId, deviceId);
        },
        async getCurrentAgentVersion() {
            return hubClient.getCurrentAgentVersion();
        },
        async getCaptureDefaults(organizationId) {
            return hubClient.getCaptureDefaults(organizationId);
        },
        async setCaptureDefaults(organizationId, defaults) {
            return hubClient.setCaptureDefaults(organizationId, defaults);
        },
        async setDeviceCaptureControls(organizationId, deviceId, controls) {
            return hubClient.setDeviceCaptureControls(organizationId, deviceId, controls);
        },
        async listDeviceSummaries(organizationId, deviceId, limit) {
            return hubClient.listDeviceSummaries(organizationId, deviceId, limit);
        },
        async listDeviceSummariesInRange(organizationId, deviceId, since, until, after = null) {
            return hubClient.listDeviceSummariesInRange(organizationId, deviceId, since, until, after);
        },
        async listDeviceDayBundles(organizationId, deviceId, from, to) {
            return hubClient.listDeviceDayBundles(organizationId, deviceId, from, to);
        },
        async sealDeviceDay(organizationId, deviceId, bundle) {
            return hubClient.sealDeviceDay(organizationId, deviceId, bundle);
        },
        async getDeviceLive(organizationId, deviceId) {
            return hubClient.getDeviceLive(organizationId, deviceId);
        },
        async listDeviceLive(organizationId) {
            return hubClient.listDeviceLive(organizationId);
        },
    },
});
